import {useQuery, useQueryClient} from '@tanstack/react-query';
import {useMemo} from 'react';
import {ConversationDetails} from '@/types/conversationMessage';
import conversationsFetchers from '@/api/fetchers/conversations';

export const CAMPAIGN_CONVERSATION_MESSAGES_QUERY_KEY =
  'campaignConversationMessages';

/**
 * Fetches conversation details (including campaign conversations and first unread message)
 * for a parent collaboration conversation, then fetches messages for each campaign conversation
 * and tags them with the campaign name.
 */
function useCampaignConversationMessages(
  conversationId: string | null,
  brandId: string
) {
  const queryClient = useQueryClient();

  const detailsQuery = useQuery({
    queryKey: [
      CAMPAIGN_CONVERSATION_MESSAGES_QUERY_KEY,
      'details',
      conversationId,
      brandId,
    ],
    queryFn: () =>
      conversationsFetchers.conversationDetails(conversationId!, brandId),
    enabled: !!conversationId,
    staleTime: 5 * 60 * 1000,
  });

  const campaignConversations = detailsQuery.data?.campaignConversations ?? [];
  const firstUnreadMessageId =
    detailsQuery.data?.firstUnreadMessage?.id ?? null;

  const campaignConversationIds = useMemo(
    () => campaignConversations.map((c) => c.id),
    [campaignConversations]
  );

  const messagesQuery = useQuery({
    queryKey: [
      CAMPAIGN_CONVERSATION_MESSAGES_QUERY_KEY,
      'messages',
      conversationId,
      campaignConversationIds,
    ],
    queryFn: async () => {
      // React Query v4 bug: the retryer re-executes queryFn using the same closure
      // from when the query was first registered — not a fresh render. If
      // campaignConversations has gone stale (e.g. empty []) by then, the retry
      // fetches nothing and the query hangs permanently in a loading state.
      // Fix: read from the cache at fetch time instead of closing over the render
      // variable, so retries always see the current campaign conversations.
      // (In v5 this is not needed — queryFn receives a fresh context on each execution.)
      const cachedDetails = queryClient.getQueryData<ConversationDetails>([
        CAMPAIGN_CONVERSATION_MESSAGES_QUERY_KEY,
        'details',
        conversationId,
        brandId,
      ]);
      const freshCampaignConversations =
        cachedDetails?.campaignConversations ?? [];

      const results = await Promise.all(
        freshCampaignConversations.map(async (cc) => {
          const msgs = await conversationsFetchers.campaignConversationMessages(
            cc.id
          );
          return msgs.map((msg) => ({
            ...msg,
            campaignName: cc.campaign?.name,
          }));
        })
      );
      return results.flat();
    },
    enabled: !!conversationId && campaignConversations.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const messages = messagesQuery.data ?? [];

  return {
    messages,
    campaignConversations,
    firstUnreadMessageId,
    isDetailsLoaded: detailsQuery.isSuccess,
    isLoading:
      (!!conversationId && detailsQuery.isLoading) ||
      (campaignConversations.length > 0 && messagesQuery.isLoading),
  };
}

export default useCampaignConversationMessages;
